import { NOTIFICATION_STATUS_CODE } from '@evorait/pace-core';
import moment from 'moment';

/**
 * The statusCode of badge.
 * Will be translated in the frontend using this value as part of the translation key.
 * This value should fit to the values in the language bundle files.
 */
export enum STATUS_BADGE {
    NEW = 'NEW',
    OVERDUE = 'OVERDUE',
    IN_PROGRESS = 'IN_PROGRESS',
    RELEASED = 'RELEASED',
    CANCELLED = 'CANCELLED',
    TO_BE_SIGNED = 'TOBESIGNED',
    PAUSED = 'PAUSED',
}

export class BadgeInfo {
    public constructor(
        public readonly badge: STATUS_BADGE,
        public readonly ignoreOverdue: boolean,
    ) {}
}

const STATUS_BADGE_MATRIX: { [key: string]: BadgeInfo } = {
    [NOTIFICATION_STATUS_CODE.DISP]: new BadgeInfo(STATUS_BADGE.NEW, true),
    [NOTIFICATION_STATUS_CODE.ETA]: new BadgeInfo(STATUS_BADGE.RELEASED, false),
    [NOTIFICATION_STATUS_CODE.DURA]: new BadgeInfo(STATUS_BADGE.RELEASED, false),
    [NOTIFICATION_STATUS_CODE.ROSR]: new BadgeInfo(STATUS_BADGE.IN_PROGRESS, true),
    [NOTIFICATION_STATUS_CODE.FCC]: new BadgeInfo(STATUS_BADGE.IN_PROGRESS, true),
    [NOTIFICATION_STATUS_CODE.TRAV]: new BadgeInfo(STATUS_BADGE.IN_PROGRESS, true),
    [NOTIFICATION_STATUS_CODE.OSR]: new BadgeInfo(STATUS_BADGE.IN_PROGRESS, true),
    [NOTIFICATION_STATUS_CODE.ATS]: new BadgeInfo(STATUS_BADGE.IN_PROGRESS, true),
    // SOSR: Special OSR for PACE US
    [NOTIFICATION_STATUS_CODE.SOSR]: new BadgeInfo(STATUS_BADGE.IN_PROGRESS, true),
    [NOTIFICATION_STATUS_CODE.AVR1]: new BadgeInfo(STATUS_BADGE.CANCELLED, true),
    [NOTIFICATION_STATUS_CODE.AVR2]: new BadgeInfo(STATUS_BADGE.CANCELLED, true),
    [NOTIFICATION_STATUS_CODE.CANC]: new BadgeInfo(STATUS_BADGE.CANCELLED, true),
    [NOTIFICATION_STATUS_CODE.TOBS]: new BadgeInfo(STATUS_BADGE.TO_BE_SIGNED, true),
    [NOTIFICATION_STATUS_CODE.RTOBS]: new BadgeInfo(STATUS_BADGE.TO_BE_SIGNED, true),
    [NOTIFICATION_STATUS_CODE.FTOBS]: new BadgeInfo(STATUS_BADGE.TO_BE_SIGNED, true),
};

/** What {@link deriveStatusBadge} needs to know about a notification. */
export interface StatusBadgeInput {
    /** The notification's effective status code. */
    statusCode: NOTIFICATION_STATUS_CODE;
    /** The status code the notification was cancelled with, or `''` when it is not cancelled. */
    isCancelled: string;
    /** Start of the notification, deciding whether it is overdue. */
    start: moment.Moment;
}

/**
 * The badge a notification is shown with, or `undefined` for a status that carries no badge
 * (CRM).
 *
 * A cancelled notification that is still in one of the pre-departure statuses is shown as
 * cancelled — the patch needed when the What's New that cancelled it is gone, e.g. after a
 * re-installation of the app.
 *
 * @param input the notification's badge-relevant fields
 * @returns the badge info, or `undefined` when the status has no badge
 */
export function deriveStatusBadge(input: StatusBadgeInput): BadgeInfo | undefined {
    let statusCode = input.statusCode;
    if (
        input.isCancelled &&
        (statusCode === NOTIFICATION_STATUS_CODE.DISP ||
            statusCode === NOTIFICATION_STATUS_CODE.ETA ||
            statusCode === NOTIFICATION_STATUS_CODE.DURA)
    ) {
        statusCode = <NOTIFICATION_STATUS_CODE>input.isCancelled;
    }

    const statusBadge = STATUS_BADGE_MATRIX[statusCode];

    // statusBadge is undefined for notifications in state CRM
    if (statusBadge && !statusBadge.ignoreOverdue && input.start.isBefore(moment())) {
        return new BadgeInfo(STATUS_BADGE.OVERDUE, false);
    }

    return statusBadge;
}
